import { ExpandLess, ExpandMore } from '@mui/icons-material';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { Button, Chip, Divider, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { ignoreGroup } from 'src/layouts/components/utils-method';
import { paths, permissions } from 'src/paths';
import { resetEditEpisodeOfCare } from 'src/redux/actions/patientEpisodeOfCare';
import { getPlanOfCare } from 'src/redux/actions/therapyPlanning';
import AvTimerIcon from '@mui/icons-material/AvTimer';

const { UPDATE_EPISODE_OF_CARE } = permissions;

type Props = {
  handleCloseEocDetailsDrawer: any;
};

export const CloseIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M17 7L7 17M7 7L17 17"
        stroke="#6C737F"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
const CalenderIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M6.75 8.25H5.25V9.75H6.75V8.25ZM9.75 8.25H8.25V9.75H9.75V8.25ZM12.75 8.25H11.25V9.75H12.75V8.25ZM14.25 3H13.5V1.5H12V3H6V1.5H4.5V3H3.75C2.9175 3 2.2575 3.675 2.2575 4.5L2.25 15C2.25 15.825 2.9175 16.5 3.75 16.5H14.25C15.075 16.5 15.75 15.825 15.75 15V4.5C15.75 3.675 15.075 3 14.25 3ZM14.25 15H3.75V6.75H14.25V15Z"
        fill="#6C737F"
      />
    </svg>
  );
};

export const PencilIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <g clip-path="url(#clip0_23997_183710)">
        <path
          d="M13.5 1.5L16.5 4.5M1.5 16.5L2.4573 12.9899C2.51976 12.7609 2.55098 12.6464 2.59892 12.5396C2.64149 12.4448 2.69379 12.3547 2.75499 12.2707C2.82391 12.1761 2.90783 12.0922 3.07568 11.9243L10.8257 4.17426C10.9742 4.02576 11.0485 3.9515 11.1341 3.92368C11.2094 3.89921 11.2906 3.89921 11.3659 3.92368C11.4515 3.9515 11.5258 4.02576 11.6743 4.17426L13.8257 6.32574C13.9742 6.47424 14.0485 6.5485 14.0763 6.63412C14.1008 6.70943 14.1008 6.79057 14.0763 6.86588C14.0485 6.9515 13.9742 7.02576 13.8257 7.17426L6.07568 14.9243C5.90783 15.0922 5.82391 15.1761 5.72931 15.245C5.64532 15.3062 5.55519 15.3585 5.46039 15.4011C5.35361 15.449 5.23911 15.4802 5.0101 15.5427L1.5 16.5Z"
          stroke="#6C737F"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_23997_183710">
          <rect
            width="18"
            height="18"
            fill="white"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

const EocDetailsDrawer = (props: Props) => {
  const { handleCloseEocDetailsDrawer } = props;
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();

  const { patients, patientDetails, episodeOfCare, editEpisodeOfCare } = paths;
  const [expanded, setExpanded] = useState(true);
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  const isTherapyPlanningPatient = useSelector(
    (state: any) => state?.getIsTherapyPlanningPatientReducer?.response?.data
  );

  const planOfCareDetails = useSelector(
    (state: any) => state?.getPlanOfCareReducer?.response?.data
  );
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);

  const planOfCareResponse = useSelector((state: any) => state?.getPlanOfCareReducer);
  const episodeOfCareData = useSelector((state: any) => state?.getSingleEpisodeOfCare?.response);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (state?.getPatientId) {
      dispatch(getPlanOfCare({ patientId: state?.getPatientId }));
    }
  }, [state?.getPatientId]);

  const eocStartDateWithoutTimezone =
    episodeOfCareData?.startDate && episodeOfCareData?.startDate?.split('T')?.at(0);
  const eocEndDateWithoutTimezone =
    episodeOfCareData?.endDate && episodeOfCareData?.endDate?.split('T')?.at(0);

  const eocPayerStartDateWithoutTimezone =
    episodeOfCareData?.payerPlanStartDate &&
    episodeOfCareData?.payerPlanStartDate?.split('T')?.at(0);

  const eocPayerEndDateWithoutTimezone =
    episodeOfCareData?.payerPlanEndDate && episodeOfCareData?.payerPlanEndDate?.split('T')?.at(0);

  const renderFrequencyDuration = (
    disipline: 'OT' | 'SLP' | 'PT',
    source: 'frequency' | 'duration'
  ) => {
    return (
      <Stack
        // gap="1rem"
        alignItems="center"
        flexDirection="row"
        position={'relative'}
        title={source}
      >
        <Chip
          variant="outlined"
          label={disipline}
        />
        <Box
          ml={8}
          position={'absolute'}
        >
          {source === 'frequency' ? <CalenderIcon /> : <AvTimerIcon />}
        </Box>
        <Typography
          position={'absolute'}
          ml={15}
        >
          {source === 'frequency'
            ? planOfCareDetails?.disciplineMap[disipline] || '-'
            : planOfCareDetails?.durationMap[disipline] || '-'}
        </Typography>
      </Stack>
    );
  };

  return (
    <Grid
      flexDirection={'column'}
      justifyContent={'space-between'}
      container
      sx={{ height: '100%' }}
    >
      <>
        <Grid xs={12}>
          <Grid
            container
            sx={{ width: '100%' }}
          >
            <Grid
              py={2}
              px={2}
              xs={12}
              sx={{ width: '100%' }}
            >
              {' '}
              <Stack
                direction="row"
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5">Episode Details & PoC</Typography>
                </Box>
                <IconButton onClick={handleCloseEocDetailsDrawer}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Divider sx={{ mt: 2.5 }} />
              <Stack p={2}>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems={'center'}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: '#4338CA' }}
                  >
                    Episode Details
                  </Typography>
                  <Box>
                    {expanded ? (
                      <Button
                        variant="text"
                        startIcon={<PencilIcon />}
                        onClick={() => {
                          dispatch(resetEditEpisodeOfCare());
                          navigate(
                            `${patients}/${patientDetails}${episodeOfCare}${editEpisodeOfCare}`,
                            {
                              state,
                            }
                          );
                        }}
                        color="inherit"
                        disabled={!permissionData?.includes(UPDATE_EPISODE_OF_CARE)}
                      >
                        Edit
                      </Button>
                    ) : null}

                    <IconButton
                      title={
                        !expanded ? 'View Episode of Care Details' : 'Hide Episode of Care Details'
                      }
                      onClick={handleExpandClick}
                    >
                      {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </Stack>

                {expanded ? (
                  <Box>
                    <Stack>
                      <Typography
                        sx={{ color: '#6C737F' }}
                        variant="overline"
                      >
                        Basic Details
                      </Typography>
                      <Box mt={2}>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography
                              noWrap
                              variant="subtitle2"
                            >
                              Episode Title
                            </Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                              noWrap
                            >
                              {episodeOfCareData?.episodeTitle ?? '-'}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">Place Of Residence</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              sx={{ color: '#6C737F' }}
                              variant="body2"
                            >
                              {episodeOfCareData?.disciplineOfComplete
                                ?.replace(/_/g, ' ')
                                ?.replace(
                                  /\w\S*/g,
                                  (txt: string) =>
                                    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
                                )}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">Medical Record Number</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                            >
                              {episodeOfCareData?.medicalRecordNumber}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">Episode Start Date</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                            >
                              {eocStartDateWithoutTimezone
                                ? format(
                                    new Date(
                                      new Date(eocStartDateWithoutTimezone).valueOf() +
                                        new Date(eocStartDateWithoutTimezone).getTimezoneOffset() *
                                          60 *
                                          1000
                                    ),
                                    'MMM dd, yyyy'
                                  )
                                : '-'}
                            </Typography>
                          </Stack>
                        </Stack>

                        {eocEndDateWithoutTimezone ? (
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Episode End Date</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {eocEndDateWithoutTimezone
                                  ? format(
                                      new Date(
                                        new Date(eocEndDateWithoutTimezone).valueOf() +
                                          new Date(eocEndDateWithoutTimezone).getTimezoneOffset() *
                                            60 *
                                            1000
                                      ),
                                      'MMM dd, yyyy'
                                    )
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>
                        ) : null}
                      </Box>
                    </Stack>
                    <Divider sx={{ mt: 2 }} />
                    <Stack mt={2}>
                      <Typography
                        sx={{ color: '#6C737F' }}
                        variant="overline"
                      >
                        Payer Details
                      </Typography>
                      <Box mt={2}>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">Payer Type</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                              noWrap
                            >
                              {episodeOfCareData?.payerType}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">Payer Name</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                              noWrap
                            >
                              {episodeOfCareData?.payerPlanEntity?.payerPlanName}
                              {episodeOfCareData?.payerPlanEntity?.active === false &&
                              episodeOfCareData?.payerPlanEntity?.payerPlanName
                                ? '- Inactive'
                                : ''}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">Start Date</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                            >
                              {eocPayerStartDateWithoutTimezone
                                ? format(
                                    new Date(
                                      new Date(eocPayerStartDateWithoutTimezone).valueOf() +
                                        new Date(
                                          eocPayerStartDateWithoutTimezone
                                        ).getTimezoneOffset() *
                                          60 *
                                          1000
                                    ),
                                    'MMM dd, yyyy'
                                  )
                                : '-'}
                            </Typography>
                          </Stack>
                        </Stack>

                        {eocPayerEndDateWithoutTimezone ? (
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">End Date</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {eocPayerEndDateWithoutTimezone
                                  ? format(
                                      new Date(
                                        new Date(eocPayerEndDateWithoutTimezone).valueOf() +
                                          new Date(
                                            eocPayerEndDateWithoutTimezone
                                          ).getTimezoneOffset() *
                                            60 *
                                            1000
                                      ),
                                      'MMM dd, yyyy'
                                    )
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>
                        ) : null}
                      </Box>
                    </Stack>
                    <Divider sx={{ mt: 2 }} />
                    <Stack mt={2}>
                      <Typography
                        sx={{ color: '#6C737F' }}
                        variant="overline"
                      >
                        Certifications
                      </Typography>
                      <Box mt={2}>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">PT Start Date</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                              noWrap
                            >
                              {episodeOfCareData?.ptCertStartDate
                                ? format(
                                  new Date(
                                    new Date(episodeOfCareData?.ptCertStartDate).valueOf() +
                                    new Date(
                                      episodeOfCareData?.ptCertStartDate
                                    ).getTimezoneOffset() *
                                    60 *
                                    1000
                                  ),
                                  'MMM dd, yyyy'
                                )
                                : '-'}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">PT End Date</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                              noWrap
                            >
                              {episodeOfCareData?.ptCertEndDate
                                ? format(
                                  new Date(
                                    new Date(episodeOfCareData?.ptCertEndDate).valueOf() +
                                    new Date(
                                      episodeOfCareData?.ptCertEndDate
                                    ).getTimezoneOffset() *
                                    60 *
                                    1000
                                  ),
                                  'MMM dd, yyyy'
                                )
                                : '-'}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">OT Start Date</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                              noWrap
                            >
                              {episodeOfCareData?.otCertStartDate
                                ? format(
                                  new Date(
                                    new Date(episodeOfCareData?.otCertStartDate).valueOf() +
                                    new Date(
                                      episodeOfCareData?.otCertStartDate
                                    ).getTimezoneOffset() *
                                    60 *
                                    1000
                                  ),
                                  'MMM dd, yyyy'
                                )
                                : '-'}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">OT End Date</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                              noWrap
                            >
                              {episodeOfCareData?.otCertEndDate
                                ? format(
                                  new Date(
                                    new Date(episodeOfCareData?.otCertEndDate).valueOf() +
                                    new Date(
                                      episodeOfCareData?.otCertEndDate
                                    ).getTimezoneOffset() *
                                    60 *
                                    1000
                                  ),
                                  'MMM dd, yyyy'
                                )
                                : '-'}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">SLP Start Date</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                              noWrap
                            >
                              {episodeOfCareData?.slpCertStartDate
                                ? format(
                                  new Date(
                                    new Date(episodeOfCareData?.slpCertStartDate).valueOf() +
                                    new Date(
                                      episodeOfCareData?.slpCertStartDate
                                    ).getTimezoneOffset() *
                                    60 *
                                    1000
                                  ),
                                  'MMM dd, yyyy'
                                )
                                : '-'}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          py={1}
                        >
                          <Stack>
                            <Typography variant="subtitle2">SLP End Date</Typography>
                          </Stack>
                          <Stack sx={{ width: '50%' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#6C737F' }}
                              noWrap
                            >
                              {episodeOfCareData?.slpCertEndDate
                                ? format(
                                  new Date(
                                    new Date(episodeOfCareData?.slpCertEndDate).valueOf() +
                                    new Date(
                                      episodeOfCareData?.slpCertEndDate
                                    ).getTimezoneOffset() *
                                    60 *
                                    1000
                                  ),
                                  'MMM dd, yyyy'
                                )
                                : '-'}
                            </Typography>
                          </Stack>
                        </Stack>

                      </Box>
                    </Stack>

                  </Box>
                ) : null}
              </Stack>
              <Divider sx={{ my: 1 }} />
              {/* plan of care */}
              <Stack p={2}>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems={'center'}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: '#4338CA' }}
                  >
                    Plan of Care
                  </Typography>
                </Stack>

                {planOfCareResponse?.loading ? (
                  <Box mt={1}>
                    {' '}
                    {['1', '2', '3'].map((sle) => {
                      return (
                        <Box
                          sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                          key={sle}
                        >
                          <Typography
                            width={'20%'}
                            variant="h1"
                          >
                            <Skeleton animation={false} />
                          </Typography>
                          <Typography
                            width={'75%'}
                            variant="h1"
                          >
                            <Skeleton animation={false} />
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Stack
                    gap="1rem"
                    mt={2}
                  >
                    <Stack gap=".75rem">
                      <Typography variant="subtitle2">Diagnosis</Typography>
                      <Stack
                        flexWrap="wrap"
                        gap="1rem"
                        flexDirection="row"
                      >
                        {planOfCareDetails?.diagnosis?.map((name: any, index: any) =>
                          name ? (
                            <React.Fragment key={index}>
                              <Typography variant="body1">{name}</Typography>
                              {index !== planOfCareDetails.diagnosis.length - 1 && (
                                <Typography variant="body1">,</Typography>
                              )}
                            </React.Fragment>
                          ) : null
                        )}
                      </Stack>
                    </Stack>
                    <Divider />
                    {renderFrequencyDuration('PT', 'frequency')}
                    {renderFrequencyDuration('OT', 'frequency')}
                    {renderFrequencyDuration('SLP', 'frequency')}
                    {/** From here */}
                    <Divider />
                    {renderFrequencyDuration('PT', 'duration')}
                    {renderFrequencyDuration('OT', 'duration')}
                    {renderFrequencyDuration('SLP', 'duration')}
                    <Button
                      variant="text"
                      disabled={!isTherapyPlanningPatient || episodeOfCareData?.status === 'CLOSED'}
                      sx={{
                        color: '#4338CA',
                        margin: '0px',
                        padding: '0px',
                        borderBottom: '2px solid #9792dd',
                        paddingBottom: '2px',
                        cursor: 'pointer',
                        borderRadius: 'unset',
                        // textDecoration: 'underline',
                        backgroundColor: 'transparent',
                        width: 'fit-content',
                      }}
                      onClick={() => {
                        navigate('/scheduling/therapy-planning/details/info', {
                          state: {
                            patientId: state?.getPatientId,
                            searchData: `${patientData?.lastName +' '+ patientData?.firstName}`,
                          },
                        });
                      }}
                    >
                      Plan Therapy &#62;
                    </Button>
                    <Divider />
                    <Stack
                      flexDirection="row"
                      gap="1rem"
                    >
                      <Box
                        sx={{
                          width: '35px',
                          height: '35px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#e8edff',
                          borderRadius: '12px',
                        }}
                      >
                        <WorkspacePremiumOutlinedIcon />
                      </Box>
                      <Stack>
                        <Typography variant="body2">Goals</Typography>
                        {planOfCareDetails?.goals?.map((goal: any) => (
                          <Typography variant="body2">{goal}</Typography>
                        ))}
                      </Stack>
                    </Stack>
                    <Divider />
                    <Stack
                      flexDirection="row"
                      gap="1rem"
                    >
                      <Box
                        sx={{
                          width: '35px',
                          height: '35px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#e8edff',
                          borderRadius: '12px',
                        }}
                      >
                        <WorkspacePremiumOutlinedIcon />
                      </Box>
                      <Stack gap=".5rem">
                        <Typography variant="body2">Codes</Typography>
                        <Stack
                          gap=".5rem"
                          flexDirection="row"
                          flexWrap={'wrap'}
                        >
                          {planOfCareDetails?.codes?.map((planOfCare: any) => (
                            <Chip
                              variant="outlined"
                              label={`${planOfCare} ${
                                planOfCareDetails?.cptCodeDescription[
                                  planOfCare?.includes('(Group)')
                                    ? ignoreGroup(planOfCare)
                                    : planOfCare
                                ]
                                  ? ` | ${planOfCareDetails?.cptCodeDescription[
                                      planOfCare?.includes('(Group)')
                                        ? ignoreGroup(planOfCare)
                                        : planOfCare
                                    ]}`
                                  : ''
                              } `}
                              size="small"
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </Stack>
                    <Divider />

                    {
                      <Stack gap=".5rem">
                        <Stack
                          gap=".5rem"
                          flexDirection="row"
                        >
                          <Typography variant="subtitle2">Concurrent Therapy : </Typography>
                          <Typography variant="body2">
                            {planOfCareDetails?.allowConcurrentTherapy ? 'Allow' : 'Not Allowed'}
                          </Typography>
                        </Stack>
                        <Stack
                          gap="1rem"
                          flexDirection="row"
                        >
                          <Typography variant="subtitle2">Group Therapy : </Typography>
                          <Typography variant="body2">
                            {planOfCareDetails?.allowGroupTherapy ? 'Allow' : 'Not Allowed'}
                          </Typography>
                        </Stack>
                      </Stack>
                    }
                  </Stack>
                )}
                <Divider sx={{ mt: 3 }} />
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </>
    </Grid>
  );
};

export default EocDetailsDrawer;
